import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import axios from "axios";
import { GlobalState } from "context/GlobalContext";
import { admin, userTypes } from "data/authData";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";
import RoleAccessSection from "../shared/RoleAccessSection";
import UserProfileFields from "../shared/UserProfileFields";
import { modalSaveBtnSx, userUi } from "../shared/userUi";

const AddNewUser = ({ allUsers, setAllUsers, setNewUserModalOpen }) => {
	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();
	const [user, setUser] = useState({ type: userTypes.internal });
	const [roles, setRoles] = useState({});

	const showSnack = (message, severity = "error") => {
		setSnackBarOpen(true);
		setSnackBar({
			...snackBar,
			message,
			severity,
			anchorOrigin: { horizontal: "center", vertical: "top" },
		});
	};

	const handleTypeChange = (type) => {
		setUser({
			...user,
			type,
			...(type === userTypes.external
				? { employee_id: "", department: "", designation: "" }
				: {}),
		});
		setRoles({});
	};

	const handleAddRole = (roleToAdd) => {
		if (roles[roleToAdd]) {
			showSnack("Role already exists");
			return;
		}
		setRoles({ ...roles, [roleToAdd]: true });
	};

	const passwordPreview =
		user.type === userTypes.external
			? `${(user.name || "").trim().split(" ").join("_")}_${user.mobile_no || ""}`
			: `${user.employee_id || ""}_${user.mobile_no || ""}`;

	const onFormSubmit = async (e) => {
		e.preventDefault();
		if (!Object.keys(roles).length) {
			showSnack("Add at least one role");
			return;
		}
		const apibody = {
			...user,
			role: roles,
			password:
				user.type === userTypes.external
					? `${user.name.split(" ").join("_")}_${user.mobile_no}`
					: `${user.employee_id}_${user.mobile_no}`,
		};
		if (user.type === userTypes.external) apibody.other_data = { root_path: Object.keys(roles)[0] };

		try {
			const res = await axios.post(`/api/auth/signup`, apibody);
			showSnack("User added", "success");
			setAllUsers([res.data.user, ...allUsers]);
			setNewUserModalOpen(false);
		} catch (err) {
			showSnack(parseErrMsgFromError(err) || "Unable to add user");
		}
	};

	return (
		<Box component="form" onSubmit={onFormSubmit} sx={{ width: "100%" }}>
			<Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, pb: 1 }}>
				<UserProfileFields user={user} setUser={setUser} onTypeChange={handleTypeChange} />
				<RoleAccessSection
					roles={roles}
					onRolesChange={setRoles}
					onAdd={handleAddRole}
					onAddAdmin={() => handleAddRole(admin)}
					userType={user.type}
				/>
				<Box
					sx={{
						width: "100%",
						px: 1.5,
						py: 1.25,
						borderRadius: 1.5,
						border: `1px dashed ${userUi.borderStrong}`,
						bgcolor: "#FAFAFA",
					}}>
					<Typography sx={{ fontSize: 13, color: userUi.textSecondary }}>
						Default password format:{" "}
						<strong>
							{user.type === userTypes.external ? "userName_phoneNo" : "employeeId_phoneNo"}
						</strong>
					</Typography>
					<Typography sx={{ fontSize: 13, color: userUi.textPrimary, mt: 0.5, fontFamily: "monospace" }}>
						{passwordPreview.replace(/^_|_$/g, "") || "—"}
					</Typography>
				</Box>
			</Box>
			<Box display="flex" justifyContent="flex-end" pt={2.5} mt={1} borderTop={`1px solid ${userUi.border}`}>
				<Button variant="contained" type="submit" sx={modalSaveBtnSx}>
					Add user
				</Button>
			</Box>
		</Box>
	);
};

export default AddNewUser;
